import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';

import { CanvasDrawingHandle, attachCanvasDrawing } from '@infrastructure/utils/canvas-drawing.util';

@Component({
  selector: 'x-signature-pad',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './x-signature-pad.component.html',
  styleUrl: './x-signature-pad.component.scss',
})
export class XSignaturePadComponent implements AfterViewInit, OnDestroy {
  label = input<string>('Signature');
  control = input.required<FormControl<string | null>>();
  width = input<number>(300);
  height = input<number>(150);

  protected readonly canvasRef = viewChild<ElementRef<HTMLCanvasElement>>('signatureCanvas');
  protected readonly hasSignature = signal(false);

  private drawingHandle: CanvasDrawingHandle | null = null;

  ngAfterViewInit(): void {
    const canvas = this.canvasRef()?.nativeElement;
    if (!canvas) {
      return;
    }

    this.drawingHandle = attachCanvasDrawing(canvas, {
      onDrawEnd: () => this.saveSignature(),
    });

    this.renderExistingSignature();
  }

  ngOnDestroy(): void {
    this.drawingHandle?.destroy();
  }

  clearSignature(): void {
    if (!this.drawingHandle) {
      return;
    }

    this.drawingHandle.clear();
    this.hasSignature.set(false);
    this.control().setValue(null);
    this.control().markAsTouched();
  }

  private saveSignature(): void {
    const canvas = this.canvasRef()?.nativeElement;
    if (!canvas) {
      return;
    }
    this.hasSignature.set(true);
    this.control().setValue(canvas.toDataURL('image/png'));
    this.control().markAsTouched();
  }

  private renderExistingSignature(): void {
    const canvas = this.canvasRef()?.nativeElement;
    const existingValue = this.control().value;
    if (!existingValue || !canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0);
      this.hasSignature.set(true);
    };
    img.src = existingValue;
  }
}
